import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getAssessments, getAssessment, createAssessment, updateAssessment, deleteAssessment, getFighters, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  Plus, 
  ClipboardList,
  ArrowLeft,
  Trash2,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Edit,
  Swords,
  Dumbbell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  Radar, 
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';

const SkillBar = ({ label, value, color = 'red', previousValue }) => {
  const getTrend = () => {
    if (previousValue === undefined || previousValue === null) return null;
    if (value > previousValue) return 'up';
    if (value < previousValue) return 'down';
    return 'same';
  };
  
  const trend = getTrend();
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-zinc-300 text-sm">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-white font-medium tabular-nums">{value}</span>
          {trend === 'up' && <TrendingUp className="w-4 h-4 text-green-500" />}
          {trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
          {trend === 'same' && <Minus className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full bg-${color}-600 rounded-full`}
          style={{ backgroundColor: color === 'red' ? '#dc2626' : color === 'green' ? '#22c55e' : color === 'blue' ? '#3b82f6' : color === 'purple' ? '#a855f7' : '#eab308' }}
        />
      </div>
    </div>
  );
};

// Dynamic Skills Display Component - Shows assessment fields based on type
const DynamicSkillsDisplay = ({ assessment, previousAssessment }) => {
  // MMA assessment fields
  const mmaFields = [
    { key: 'striking', label: 'Striking', color: 'red' },
    { key: 'grappling', label: 'Grappling', color: 'blue' },
    { key: 'wrestling', label: 'Wrestling', color: 'green' },
    { key: 'cardio', label: 'Cardio', color: 'yellow' },
    { key: 'clinch_work', label: 'Clinch Work', color: 'red' },
    { key: 'takedown_defense', label: 'Takedown Defense', color: 'green' },
    { key: 'ground_and_pound', label: 'Ground & Pound', color: 'red' },
    { key: 'submission_defense', label: 'Submission Defense', color: 'blue' },
    { key: 'cage_control', label: 'Cage Control', color: 'purple' },
    { key: 'fight_iq', label: 'Fight IQ', color: 'yellow' },
    { key: 'conditioning', label: 'Conditioning', color: 'green' },
    { key: 'mental_toughness', label: 'Mental Toughness', color: 'purple' }
  ];
  
  // BJJ assessment fields
  const bjjFields = [
    { key: 'guard_play', label: 'Guard Play', color: 'blue' },
    { key: 'passing', label: 'Passing', color: 'green' },
    { key: 'sweeps', label: 'Sweeps', color: 'blue' },
    { key: 'submissions', label: 'Submissions', color: 'red' },
    { key: 'takedowns', label: 'Takedowns', color: 'green' },
    { key: 'escapes', label: 'Escapes', color: 'yellow' },
    { key: 'positional_control', label: 'Positional Control', color: 'purple' },
    { key: 'competition_mindset', label: 'Competition Mindset', color: 'yellow' }
  ];
  
  const fields = assessment.assessment_type === 'bjj' ? bjjFields : mmaFields;
  
  // Filter to only show fields that have values
  const activeFields = fields.filter(f => assessment[f.key] !== undefined && assessment[f.key] !== null);
  
  // If no new format fields, fall back to legacy display
  if (activeFields.length === 0) {
    // Legacy MMA format
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {assessment.striking !== undefined && (
          <SkillBar label="Striking" value={assessment.striking} color="red" previousValue={previousAssessment?.striking} />
        )}
        {assessment.grappling !== undefined && (
          <SkillBar label="Grappling" value={assessment.grappling} color="blue" previousValue={previousAssessment?.grappling} />
        )}
        {assessment.wrestling !== undefined && (
          <SkillBar label="Wrestling" value={assessment.wrestling} color="green" previousValue={previousAssessment?.wrestling} />
        )}
        {assessment.cardio !== undefined && (
          <SkillBar label="Cardio" value={assessment.cardio} color="yellow" previousValue={previousAssessment?.cardio} />
        )}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {activeFields.map(field => (
        <SkillBar 
          key={field.key}
          label={field.label} 
          value={assessment[field.key]} 
          color={field.color}
          previousValue={previousAssessment?.[field.key]}
        />
      ))}
    </div>
  );
};

// Assessments List Page
export const AssessmentsListPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterFighterId = searchParams.get('fighter');
  
  const [assessments, setAssessments] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFighter, setSelectedFighter] = useState(filterFighterId || 'all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
    trackFeatureUsage('assessments', 'view').catch(() => {});
  }, [selectedFighter]);

  const loadData = async () => {
    try {
      const fighterId = selectedFighter !== 'all' ? selectedFighter : null;
      const [assessmentsRes, fightersRes] = await Promise.all([
        getAssessments(fighterId),
        getFighters()
      ]);
      setAssessments(assessmentsRes.data);
      setFighters(fightersRes.data);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteAssessment(deleteId);
      toast.success('Assessment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete assessment');
    } finally {
      setDeleteId(null);
    }
  };

  // Prepare chart data for selected fighter - uses new assessment criteria
  const getChartData = () => {
    if (selectedFighter === 'all' || assessments.length === 0) return null;
    
    // Check if assessments use new format
    const hasNewFormat = assessments.some(a => 
      a.assessment_type === 'mma' || a.assessment_type === 'bjj' ||
      a.striking_offense || a.guard_play
    );
    
    if (!hasNewFormat) {
      // Legacy format
      return {
        type: 'legacy',
        data: assessments.slice(0, 10).reverse().map(a => ({
          date: format(parseISO(a.created_at), 'MMM d'),
          striking: a.striking,
          grappling: a.grappling,
          wrestling: a.wrestling,
          cardio: a.cardio
        }))
      };
    }
    
    // Determine dominant type
    const mmaCount = assessments.filter(a => a.assessment_type !== 'bjj').length;
    const bjjCount = assessments.filter(a => a.assessment_type === 'bjj').length;
    const isMMA = mmaCount >= bjjCount;
    
    const filtered = assessments.filter(a => 
      isMMA ? a.assessment_type !== 'bjj' : a.assessment_type === 'bjj'
    ).slice(0, 10).reverse();
    
    if (isMMA) {
      return {
        type: 'mma',
        data: filtered.map(a => ({
          date: format(parseISO(a.created_at), 'MMM d'),
          striking_offense: a.striking_offense || 0,
          striking_defense: a.striking_defense || 0,
          wrestling_offense: a.wrestling_offense || 0,
          takedown_defense: a.takedown_defense || 0,
          ground_control: a.ground_control || 0,
          fight_iq: a.fight_iq || 0
        }))
      };
    } else {
      return {
        type: 'bjj',
        data: filtered.map(a => ({
          date: format(parseISO(a.created_at), 'MMM d'),
          guard_play: a.guard_play || 0,
          passing: a.passing || 0,
          submissions: a.submissions || 0,
          sweeps: a.sweeps || 0,
          takedowns: a.takedowns || 0,
          escapes: a.escapes || 0
        }))
      };
    }
  };

  const chartResult = getChartData();
  const chartData = chartResult?.data;
  const chartType = chartResult?.type;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="assessments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">
              Performance Tracking
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Skill Assessments
            </h1>
            <p className="text-zinc-400 mt-1">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''} recorded</p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/assessments/new')}
              className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading font-semibold tracking-wide"
              data-testid="add-assessment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Run Assessment
            </Button>
          )}
        </div>

        {/* Filter */}
        <div className="flex gap-4">
          <Select value={selectedFighter} onValueChange={setSelectedFighter}>
            <SelectTrigger className="w-64 h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-filter">
              <SelectValue placeholder="Filter by fighter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fighters</SelectItem>
              {fighters.map(fighter => (
                <SelectItem key={fighter.id} value={fighter.id}>
                  {fighter.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Progress Chart (when fighter selected) */}
        {chartData && chartData.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
          >
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              Progress Over Time {chartType === 'mma' ? '(MMA)' : chartType === 'bjj' ? '(BJJ)' : ''}
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.9)', 
                      border: '1px solid #dc2626',
                      borderRadius: '4px'
                    }}
                  />
                  {chartType === 'legacy' && (
                    <>
                      <Line type="monotone" dataKey="striking" stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626' }} />
                      <Line type="monotone" dataKey="grappling" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                      <Line type="monotone" dataKey="wrestling" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                      <Line type="monotone" dataKey="cardio" stroke="#eab308" strokeWidth={2} dot={{ fill: '#eab308' }} />
                    </>
                  )}
                  {chartType === 'mma' && (
                    <>
                      <Line type="monotone" dataKey="striking_offense" name="Striking Off." stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626' }} />
                      <Line type="monotone" dataKey="striking_defense" name="Striking Def." stroke="#f87171" strokeWidth={2} dot={{ fill: '#f87171' }} />
                      <Line type="monotone" dataKey="wrestling_offense" name="Wrestling Off." stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                      <Line type="monotone" dataKey="takedown_defense" name="TD Defense" stroke="#4ade80" strokeWidth={2} dot={{ fill: '#4ade80' }} />
                      <Line type="monotone" dataKey="ground_control" name="Ground Ctrl" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                      <Line type="monotone" dataKey="fight_iq" name="Fight IQ" stroke="#eab308" strokeWidth={2} dot={{ fill: '#eab308' }} />
                    </>
                  )}
                  {chartType === 'bjj' && (
                    <>
                      <Line type="monotone" dataKey="guard_play" name="Guard Play" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                      <Line type="monotone" dataKey="passing" name="Passing" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                      <Line type="monotone" dataKey="submissions" name="Submissions" stroke="#dc2626" strokeWidth={2} dot={{ fill: '#dc2626' }} />
                      <Line type="monotone" dataKey="sweeps" name="Sweeps" stroke="#a855f7" strokeWidth={2} dot={{ fill: '#a855f7' }} />
                      <Line type="monotone" dataKey="takedowns" name="Takedowns" stroke="#14b8a6" strokeWidth={2} dot={{ fill: '#14b8a6' }} />
                      <Line type="monotone" dataKey="escapes" name="Escapes" stroke="#eab308" strokeWidth={2} dot={{ fill: '#eab308' }} />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
              {chartType === 'legacy' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Striking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Grappling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Wrestling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <span className="text-zinc-400 text-sm">Cardio</span>
                  </div>
                </>
              )}
              {chartType === 'mma' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Strike Off.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <span className="text-zinc-400 text-sm">Strike Def.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Wrestle Off.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full" />
                    <span className="text-zinc-400 text-sm">TD Defense</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Ground</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <span className="text-zinc-400 text-sm">Fight IQ</span>
                  </div>
                </>
              )}
              {chartType === 'bjj' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Guard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Passing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-600 rounded-full" />
                    <span className="text-zinc-400 text-sm">Subs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full" />
                    <span className="text-zinc-400 text-sm">Sweeps</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-teal-500 rounded-full" />
                    <span className="text-zinc-400 text-sm">TDs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <span className="text-zinc-400 text-sm">Escapes</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Assessments List */}
        {assessments.length > 0 ? (
          <div className="space-y-4">
            {assessments.map((assessment, index) => {
              const previousAssessment = assessments[index + 1];
              return (
                <motion.div
                  key={assessment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm"
                  data-testid={`assessment-card-${assessment.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                    <div>
                      <Link 
                        to={`/fighters/${assessment.fighter_id}`}
                        className="text-lg text-white font-medium hover:text-red-500 flex items-center gap-2"
                      >
                        <Users className="w-5 h-5" />
                        {getFighterName(assessment.fighter_id)}
                      </Link>
                      <p className="text-zinc-500 text-sm mt-1">
                        {format(parseISO(assessment.created_at), 'M/d/yyyy')}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/assessments/edit/${assessment.id}`)}
                          className="text-zinc-400 hover:text-blue-500"
                          data-testid={`edit-assessment-${assessment.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(assessment.id)}
                          className="text-zinc-400 hover:text-red-500"
                          data-testid={`delete-assessment-${assessment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Assessment Type Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    {assessment.assessment_type === 'bjj' ? (
                      <span className="px-2 py-1 text-xs uppercase font-medium rounded-sm border bg-blue-600/20 text-blue-500 border-blue-600/30 flex items-center gap-1">
                        <Dumbbell className="w-3 h-3" />
                        BJJ {assessment.gi_nogi === 'nogi' ? '(No-Gi)' : assessment.gi_nogi === 'gi' ? '(Gi)' : ''}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs uppercase font-medium rounded-sm border bg-red-600/20 text-red-500 border-red-600/30 flex items-center gap-1">
                        <Swords className="w-3 h-3" />
                        MMA
                      </span>
                    )}
                  </div>
                  
                  {/* Dynamic Skills Display */}
                  <DynamicSkillsDisplay 
                    assessment={assessment} 
                    previousAssessment={previousAssessment}
                  />
                  
                  {assessment.notes && (
                    <p className="text-zinc-500 text-sm mt-4 pt-4 border-t border-zinc-800">
                      {assessment.notes}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-16 px-8 text-center bg-zinc-900/30 border border-zinc-800 rounded-sm"
          >
            <div className="w-16 h-16 bg-red-600/20 border border-red-600/30 rounded-sm flex items-center justify-center mb-6">
              <ClipboardList className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="font-heading text-2xl text-white uppercase tracking-tight mb-2">
              Track Fighter Progress
            </h3>
            <p className="text-zinc-400 mb-6 max-w-md">
              Assessments help you measure and track each fighter's skills over time. 
              Run regular assessments to identify strengths, weaknesses, and improvement areas.
            </p>
            {isAdmin && (
              <Button 
                onClick={() => navigate('/assessments/new')}
                className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow uppercase font-heading tracking-wider px-6"
                data-testid="run-first-assessment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Run First Assessment
              </Button>
            )}
          </motion.div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Assessment</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this assessment? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

// Assessment Form Page (Create and Edit)
export const AssessmentFormPage = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id: assessmentId } = useParams(); // For edit mode
  const [searchParams] = useSearchParams();
  const preselectedFighter = searchParams.get('fighter');
  const { isAdmin } = useAuth();
  
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sportConfig, setSportConfig] = useState(null);
  const [assessmentType, setAssessmentType] = useState('default'); // 'mma', 'bjj', or 'default'
  const [giNogi, setGiNogi] = useState(null); // 'gi' or 'nogi' for BJJ
  const [selectedFighter, setSelectedFighter] = useState(null); // Track selected fighter's data
  const [formData, setFormData] = useState({
    fighter_id: preselectedFighter || '',
    notes: ''
  });

  // Skill colors for visual variety
  const skillColors = [
    'red', 'blue', 'green', 'yellow', 'purple', 'orange', 
    'pink', 'cyan', 'emerald', 'amber', 'violet', 'teal'
  ];
  
  const colorClasses = {
    red: '[&_[role=slider]]:bg-red-600',
    blue: '[&_[role=slider]]:bg-blue-600',
    green: '[&_[role=slider]]:bg-green-600',
    yellow: '[&_[role=slider]]:bg-yellow-500',
    purple: '[&_[role=slider]]:bg-purple-600',
    orange: '[&_[role=slider]]:bg-orange-500',
    pink: '[&_[role=slider]]:bg-pink-500',
    cyan: '[&_[role=slider]]:bg-cyan-500',
    emerald: '[&_[role=slider]]:bg-emerald-500',
    amber: '[&_[role=slider]]:bg-amber-500',
    violet: '[&_[role=slider]]:bg-violet-500',
    teal: '[&_[role=slider]]:bg-teal-500',
  };

  useEffect(() => {
    loadData();
  }, [assessmentId]);

  const loadData = async () => {
    try {
      const [fightersRes, configRes] = await Promise.all([
        getFighters(),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gym/sport-config`)
      ]);
      setFighters(fightersRes.data);
      setSportConfig(configRes.data);
      
      // If editing, load the existing assessment
      if (isEdit && assessmentId) {
        const assessmentRes = await getAssessment(assessmentId);
        const existing = assessmentRes.data;
        setAssessmentType(existing.assessment_type || 'mma');
        setGiNogi(existing.gi_nogi || null);
        setFormData(existing);
        
        // Set the selected fighter for edit mode
        const fighter = fightersRes.data.find(f => f.id === existing.fighter_id);
        setSelectedFighter(fighter || null);
      } else {
        // Set default assessment type based on gym's sport type
        const sportType = configRes.data?.sport_type || 'mma';
        if (sportType === 'hybrid') {
          setAssessmentType('mma'); // Default to MMA for hybrid, user can toggle
        } else {
          setAssessmentType(sportType);
        }
        
        // If preselected fighter, set it and adjust assessment type
        if (preselectedFighter) {
          const fighter = fightersRes.data.find(f => f.id === preselectedFighter);
          setSelectedFighter(fighter || null);
          // If fighter has specific sports, default to their first sport
          if (fighter?.sport_types?.length > 0) {
            const defaultSport = fighter.sport_types.includes('mma') ? 'mma' : 'bjj';
            setAssessmentType(defaultSport);
          }
        }
        
        // Initialize form data with default values for all fields
        const fields = getAssessmentFields(configRes.data, sportType === 'hybrid' ? 'mma' : sportType);
        const initialValues = {};
        fields.forEach(f => {
          initialValues[f.key] = 50;
        });
        setFormData(prev => ({ ...prev, ...initialValues }));
      }
      
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getAssessmentFields = (config, type) => {
    if (!config) return [];
    const cfg = config.assessment_config;
    if (type === 'bjj' && cfg.bjj_fields) return cfg.bjj_fields;
    if (type === 'mma' && cfg.mma_fields) return cfg.mma_fields;
    return cfg.fields || [];
  };

  // Handle fighter selection change
  const handleFighterChange = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    setSelectedFighter(fighter || null);
    setFormData({ ...formData, fighter_id: fighterId });
    
    // If fighter has specific sport types, update assessment type to match
    if (fighter?.sport_types?.length > 0) {
      // If current assessment type is not in fighter's sports, switch to their first sport
      if (!fighter.sport_types.includes(assessmentType)) {
        const newType = fighter.sport_types.includes('mma') ? 'mma' : 'bjj';
        handleAssessmentTypeChange(newType);
      }
    }
  };

  const handleAssessmentTypeChange = (type) => {
    setAssessmentType(type);
    // Reset values for new type's fields
    const fields = getAssessmentFields(sportConfig, type);
    const newValues = { fighter_id: formData.fighter_id, notes: formData.notes };
    fields.forEach(f => {
      newValues[f.key] = formData[f.key] || 50;
    });
    setFormData(newValues);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        assessment_type: assessmentType,
        gi_nogi: assessmentType === 'bjj' ? giNogi : null
      };
      
      if (isEdit && assessmentId) {
        await updateAssessment(assessmentId, submitData);
        toast.success('Assessment updated');
      } else {
        await createAssessment(submitData);
        toast.success('Assessment created');
      }
      navigate('/assessments');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save assessment'));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    navigate('/assessments');
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const currentFields = getAssessmentFields(sportConfig, assessmentType);
  const radarData = currentFields.map(f => ({
    skill: f.label.split(' ')[0], // Shorten for radar
    value: formData[f.key] || 50
  }));

  const isHybrid = sportConfig?.sport_type === 'hybrid' || sportConfig?.sport_type === 'platinum';
  
  // Check if selected fighter competes in multiple sports
  const fighterHasMultipleSports = selectedFighter?.sport_types?.length > 1;
  const fighterSports = selectedFighter?.sport_types || [];
  
  // Show sport toggle if gym is hybrid/platinum OR fighter has multiple sports
  const showSportToggle = isHybrid || fighterHasMultipleSports;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="assessment-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/assessments')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Assessments
        </Button>

        <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-2">
          {isEdit ? 'Edit Assessment' : 'New Assessment'}
        </h1>
        <p className="text-zinc-400 mb-8">
          {assessmentType === 'bjj' ? 'Brazilian Jiu-Jitsu' : 
           assessmentType === 'mma' ? 'Mixed Martial Arts' : 
           sportConfig?.sport_type === 'bjj' ? 'Brazilian Jiu-Jitsu' :
           sportConfig?.sport_type === 'mma' ? 'Mixed Martial Arts' :
           'Multi-Sport'} Assessment
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fighter" className="text-zinc-300">Fighter *</Label>
              <Select 
                value={formData.fighter_id} 
                onValueChange={handleFighterChange}
              >
                <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="assessment-fighter-select">
                  <SelectValue placeholder="Select fighter" />
                </SelectTrigger>
                <SelectContent>
                  {fighters.map(fighter => (
                    <SelectItem key={fighter.id} value={fighter.id}>
                      <div className="flex items-center gap-2">
                        {fighter.name} - {fighter.weight_class}
                        {fighter.bjj_belt && ` (${fighter.bjj_belt} belt)`}
                        {fighter.sport_types?.length > 1 && (
                          <span className="ml-2 text-xs bg-purple-600/20 text-purple-400 px-1.5 py-0.5 rounded">
                            Multi-Sport
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFighter && selectedFighter.sport_types?.length > 1 && (
                <p className="text-xs text-purple-400">
                  🏆 {selectedFighter.name} competes in: {selectedFighter.sport_types.map(s => s.toUpperCase()).join(' & ')}
                </p>
              )}
            </div>

            {/* Show sport toggle for hybrid gyms OR multi-sport fighters */}
            {showSportToggle && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Assessment Type</Label>
                {fighterHasMultipleSports && !isHybrid && (
                  <p className="text-xs text-zinc-500">This fighter competes in multiple sports</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleAssessmentTypeChange('mma')}
                    disabled={fighterHasMultipleSports && !fighterSports.includes('mma')}
                    className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                      assessmentType === 'mma'
                        ? 'border-red-600 bg-red-600/10 text-white'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    } ${fighterHasMultipleSports && !fighterSports.includes('mma') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="assessment-type-mma"
                  >
                    <Swords className="w-5 h-5" />
                    MMA
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAssessmentTypeChange('bjj')}
                    disabled={fighterHasMultipleSports && !fighterSports.includes('bjj')}
                    className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                      assessmentType === 'bjj'
                        ? 'border-blue-600 bg-blue-600/10 text-white'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    } ${fighterHasMultipleSports && !fighterSports.includes('bjj') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="assessment-type-bjj"
                  >
                    <Dumbbell className="w-5 h-5" />
                    BJJ
                  </button>
                </div>
              </div>
            )}

            {/* Gi/No-Gi toggle for BJJ assessments */}
            {assessmentType === 'bjj' && sportConfig?.assessment_config?.has_gi_nogi && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Training Type</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setGiNogi('gi')}
                    className={`flex-1 py-2 px-4 rounded-sm border text-sm transition-all ${
                      giNogi === 'gi'
                        ? 'border-blue-600 bg-blue-600/10 text-white'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    }`}
                  >
                    Gi
                  </button>
                  <button
                    type="button"
                    onClick={() => setGiNogi('nogi')}
                    className={`flex-1 py-2 px-4 rounded-sm border text-sm transition-all ${
                      giNogi === 'nogi'
                        ? 'border-purple-600 bg-purple-600/10 text-white'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    }`}
                  >
                    No-Gi
                  </button>
                </div>
              </div>
            )}

            {/* Dynamic skill sliders */}
            <div className="space-y-5 pt-4 max-h-[400px] overflow-y-auto pr-2">
              {currentFields.map((field, idx) => (
                <div key={field.key} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-zinc-300">{field.label}</Label>
                      {field.description && (
                        <p className="text-xs text-zinc-500 mt-0.5">{field.description}</p>
                      )}
                    </div>
                    <span className="text-white font-medium tabular-nums w-12 text-right">
                      {formData[field.key] || 50}
                    </span>
                  </div>
                  <Slider
                    value={[formData[field.key] || 50]}
                    onValueChange={([value]) => setFormData({ ...formData, [field.key]: value })}
                    max={100}
                    step={1}
                    className={colorClasses[skillColors[idx % skillColors.length]]}
                    data-testid={`${field.key}-slider`}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-zinc-300">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional observations..."
                className="min-h-[100px] bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="assessment-notes-input"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/assessments')}
                className="flex-1 h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-900"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !formData.fighter_id}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
                data-testid="save-assessment-btn"
              >
                {saving ? 'Saving...' : isEdit ? 'Update Assessment' : 'Save Assessment'}
              </Button>
            </div>
          </form>

          {/* Live Preview Radar */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              Preview
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#3f3f46" />
                  <PolarAngleAxis dataKey="skill" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
                  <Radar
                    name="Skills"
                    dataKey="value"
                    stroke={assessmentType === 'bjj' ? '#3b82f6' : '#dc2626'}
                    fill={assessmentType === 'bjj' ? '#3b82f6' : '#dc2626'}
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
